import { createRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { IconPlus, IconListCheck } from '@tabler/icons-react';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import {
  useAddChecklistItem,
  useChecklist,
  useEditChecklistItem,
  useSetChecklistItemActive,
  type ChecklistItem,
} from '../../../../hooks/useChecklist.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { SendModalShell } from '../../../../components/send/SendModalShell.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/checklist',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: ChecklistEditorPage,
});

/**
 * A pending mutation, held until the confirm dialog is accepted. Every write is
 * live to EVERY branch's next send the instant it commits, so nothing mutates
 * until the super_admin confirms in the modal.
 */
type Pending =
  | { kind: 'add'; label: string }
  | { kind: 'edit'; id: string; label: string; previous: string }
  | { kind: 'toggle'; id: string; label: string; active: boolean };

type Banner = { tone: 'success' | 'error'; message: string } | null;

export function ChecklistEditorPage() {
  const { data, isLoading, error } = useChecklist();
  const add = useAddChecklistItem();
  const edit = useEditChecklistItem();
  const setActive = useSetChecklistItemActive();

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newLabel, setNewLabel] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);

  const items = useMemo(
    () => (data?.items ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [data],
  );

  const busy = add.isPending || edit.isPending || setActive.isPending;

  function draftFor(item: ChecklistItem): string {
    return drafts[item.id] ?? item.label;
  }

  function openEdit(item: ChecklistItem) {
    const label = draftFor(item).trim();
    if (!label || label === item.label) return;
    setPending({ kind: 'edit', id: item.id, label, previous: item.label });
    setConfirming(true);
  }

  function openToggle(item: ChecklistItem) {
    setPending({ kind: 'toggle', id: item.id, label: item.label, active: !item.active });
    setConfirming(true);
  }

  function openAdd() {
    const label = newLabel.trim();
    if (!label) return;
    setPending({ kind: 'add', label });
    setConfirming(true);
  }

  async function confirm() {
    if (!pending) return;
    setBanner(null);
    try {
      if (pending.kind === 'add') {
        await add.mutateAsync(pending.label);
        setNewLabel('');
        setBanner({ tone: 'success', message: `Added “${pending.label}” to the checklist.` });
      } else if (pending.kind === 'edit') {
        await edit.mutateAsync({ id: pending.id, label: pending.label });
        setDrafts((d) => {
          const next = { ...d };
          delete next[pending.id];
          return next;
        });
        setBanner({ tone: 'success', message: 'Checklist item updated for every branch.' });
      } else {
        await setActive.mutateAsync({ id: pending.id, active: pending.active });
        setBanner({
          tone: 'success',
          message: pending.active ? 'Item enabled.' : 'Item disabled.',
        });
      }
      setConfirming(false);
      setPending(null);
    } catch (e) {
      setBanner({ tone: 'error', message: (e as Error).message });
      setConfirming(false);
    }
  }

  function confirmText(p: Pending | null): string {
    if (!p) return '';
    if (p.kind === 'add') return `Add “${p.label}” to the checklist?`;
    if (p.kind === 'edit') return `Change “${p.previous}” to “${p.label}”?`;
    return p.active ? `Enable “${p.label}”?` : `Disable “${p.label}”?`;
  }

  return (
    <div>
      <AdminPageHeader
        title="Checklist"
        description="The master “Required documents & details” list. Every change here flows to all branches' next send instantly — there is one list for the whole group."
      />

      {banner && (
        <div
          role="status"
          className={
            banner.tone === 'success'
              ? 'mb-6 rounded-lg border border-success/30 bg-success-bg px-4 py-3 text-sm text-success'
              : 'mb-6 rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger'
          }
        >
          {banner.message}
        </div>
      )}

      {isLoading && <p className="text-muted">Loading…</p>}
      {error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load the checklist: {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="max-w-3xl">
          <div className="bg-white border border-line rounded-xl divide-y divide-line">
            {items.map((item) => {
              const changed = draftFor(item).trim() !== item.label;
              return (
                <div
                  key={item.id}
                  data-testid="checklist-row"
                  data-active={item.active}
                  className={
                    item.active
                      ? 'flex items-center gap-3 px-4 py-3'
                      : 'flex items-center gap-3 px-4 py-3 opacity-50'
                  }
                >
                  <span className="w-6 shrink-0 text-xs text-muted tabular-nums">{item.sortOrder}</span>
                  <div className="flex-1">
                    <Input
                      aria-label={`Edit label for item ${item.sortOrder}`}
                      value={draftFor(item)}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [item.id]: e.target.value }))
                      }
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!changed || busy}
                    onClick={() => openEdit(item)}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant={item.active ? 'ghost' : 'primary'}
                    disabled={busy}
                    onClick={() => openToggle(item)}
                  >
                    {item.active ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              );
            })}
            {items.length === 0 && (
              <div className="px-4 py-12 text-center">
                <IconListCheck size={28} className="text-muted-soft mx-auto mb-3" />
                <p className="text-ink-soft font-medium">No checklist items yet</p>
                <p className="text-sm text-muted mt-1">Add the first item below.</p>
              </div>
            )}
          </div>

          {/* Add-new item — appends at the end of the list. */}
          <div className="mt-6 flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="new-checklist-item" className="block text-sm font-medium text-ink-soft mb-1.5">
                Add a new item
              </label>
              <Input
                id="new-checklist-item"
                placeholder="e.g. Statement of purpose"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </div>
            <Button
              leftIcon={<IconPlus size={16} />}
              disabled={!newLabel.trim() || busy}
              onClick={openAdd}
            >
              Add item
            </Button>
          </div>
        </div>
      )}

      <SendModalShell open={confirming} onClose={() => setConfirming(false)} blocking={busy}>
        <div className="p-6">
          <h2 className="font-serif text-xl text-ink">Apply to every branch?</h2>
          <p className="mt-2 text-sm text-ink-soft">{confirmText(pending)}</p>
          <p className="mt-3 text-xs text-muted">
            This is live to every branch's next send the moment you confirm.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={confirm} loading={busy}>
              Confirm
            </Button>
          </div>
        </div>
      </SendModalShell>
    </div>
  );
}
